export function formatRequests({ n }: { n: number }): string {
  if (n >= 999_950) return `${(n / 1_000_000).toFixed(1)}M`; // prevent "1000.0K" rounding
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function formatLatency({ ms }: { ms: number }): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
}

export function formatErrorRate({ rate }: { rate: number }): string {
  return `${rate.toFixed(1)}%`;
}

export function formatAudioHours({ seconds }: { seconds: number }): string {
  if (seconds === 0) return "0h";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export function formatDuration({ ms }: { ms: number | undefined }): string {
  if (ms === undefined) return "—";
  return formatLatency({ ms });
}

export type Granularity = "minute" | "hour" | "day" | "week";

/** Formats UTC bucket labels; weekly buckets display their full seven-day span. */
export function formatXAxisLabel({
  timestamp,
  granularity,
}: {
  timestamp: string;
  granularity: Granularity;
}): string {
  const d = new Date(timestamp);
  if (granularity === "minute" || granularity === "hour") {
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  }
  if (granularity === "week") {
    // Show the full week range (Mon–Sun) so users can see current week includes today
    const end = new Date(d);
    end.setUTCDate(d.getUTCDate() + 6);
    const startStr = d.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    const endStr =
      end.getUTCMonth() === d.getUTCMonth()
        ? end.toLocaleDateString([], { day: "numeric", timeZone: "UTC" })
        : end.toLocaleDateString([], {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
    return `${startStr}–${endStr}`;
  }
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function statusCodeVariant({
  code,
}: {
  code: number;
}): "default" | "secondary" | "destructive" | "outline" {
  if (code >= 500) return "destructive";
  if (code >= 400) return "outline";
  return "secondary";
}

export function errorRateVariant({
  rate,
}: {
  rate: number;
}): "default" | "secondary" | "destructive" {
  if (rate > 5) return "destructive";
  if (rate >= 1) return "secondary";
  return "default";
}
